import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ArrowBack, Send, CheckCircle, ExpandLess, ExpandMore } from '@mui/icons-material';
import { collection, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { format, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { CheckinRecord, RouteMonthlyCheckin } from '../types/boarding';
import { Inquiry, InquiryDiagnostics } from '../types/inquiry';
import { Route } from '../types/route';
import { sendInquiryAnswerNotification } from '../services/fcmService';

interface LocationState {
  from?: string;
  page?: number;
  filter?: 'all' | 'pending' | 'answered';
}

interface RecentCheckin extends CheckinRecord {
  id: string;
  lineName: string;
  routeId: string;
}

const RECENT_CHECKIN_LIMIT = 15;

export const InquiryDetailPage: React.FC = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, adminType } = useAuth();

  // 이전 페이지 정보 가져오기
  const locationState = location.state as LocationState | null;

  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [answeredByName, setAnsweredByName] = useState<string>('');
  const [answeredByAdminType, setAnsweredByAdminType] = useState<string>('');
  const [isDiagnosticsOpen, setIsDiagnosticsOpen] = useState(false);
  const [pastInquiries, setPastInquiries] = useState<Inquiry[]>([]);
  const [pastInquiriesLoading, setPastInquiriesLoading] = useState(false);
  const [pastInquiriesError, setPastInquiriesError] = useState('');
  const [isPastInquiriesOpen, setIsPastInquiriesOpen] = useState(false);
  const [recentCheckins, setRecentCheckins] = useState<RecentCheckin[]>([]);
  const [recentCheckinsLoading, setRecentCheckinsLoading] = useState(false);
  const [recentCheckinsError, setRecentCheckinsError] = useState('');
  const [isRecentCheckinsOpen, setIsRecentCheckinsOpen] = useState(false);

  // 실시간 민원 정보 구독
  useEffect(() => {
    if (!inquiryId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'inquiries', inquiryId),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = { id: docSnapshot.id, ...docSnapshot.data() } as Inquiry;
          setInquiry(data);

          // 이미 답변이 있으면 답변 내용 표시
          if (data.answer) {
            setAnswer(data.answer);
          }
        } else {
          setError('민원을 찾을 수 없습니다.');
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching inquiry:', err);
        setError('민원 정보를 불러오는데 실패했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [inquiryId]);

  // 같은 민원인의 과거 민원 내역 조회 (단순문의 제외)
  useEffect(() => {
    const fetchPastInquiries = async () => {
      if (!inquiry?.userId || !inquiryId) {
        setPastInquiries([]);
        return;
      }

      setPastInquiriesLoading(true);
      setPastInquiriesError('');

      try {
        const pastInquiryQuery = query(
          collection(db, 'inquiries'),
          where('userId', '==', inquiry.userId),
          where('category', 'in', ['노선', '서비스'])
        );
        const snapshot = await getDocs(pastInquiryQuery);
        const items = snapshot.docs
          .map((docSnapshot) => ({
            id: docSnapshot.id,
            ...docSnapshot.data(),
          }) as Inquiry)
          .filter((item) => item.id !== inquiryId)
          .sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() ?? 0;
            const bTime = b.createdAt?.toMillis?.() ?? 0;
            return bTime - aTime;
          });

        setPastInquiries(items);
      } catch (error) {
        console.error('[과거 민원 내역] 조회 오류:', error);
        setPastInquiries([]);
        setPastInquiriesError('과거 민원 내역을 불러오는데 실패했습니다.');
      } finally {
        setPastInquiriesLoading(false);
      }
    };

    fetchPastInquiries();
  }, [inquiry?.userId, inquiryId]);

  // 같은 민원인의 최근 탑승 기록 조회
  useEffect(() => {
    const fetchRecentCheckins = async () => {
      if (!inquiry?.userId) {
        setRecentCheckins([]);
        return;
      }

      setRecentCheckinsLoading(true);
      setRecentCheckinsError('');

      try {
        const fetchCheckinsForMonth = async (yearMonth: string): Promise<RecentCheckin[]> => {
          const checkinQuery = query(
            collection(db, 'route_monthly_checkins'),
            where('year_month', '==', yearMonth),
            where('unique_users', 'array-contains', inquiry.userId)
          );
          const snapshot = await getDocs(checkinQuery);

          return snapshot.docs.flatMap((docSnapshot) => {
            const monthlyCheckins = docSnapshot.data() as RouteMonthlyCheckin;
            const yearMonthSuffix = `_${monthlyCheckins.year_month}`;
            const routeId = docSnapshot.id.endsWith(yearMonthSuffix)
              ? docSnapshot.id.slice(0, -yearMonthSuffix.length)
              : docSnapshot.id.split('_')[0];

            return (monthlyCheckins.checkins || [])
              .filter((checkin) => checkin.uid === inquiry.userId)
              .map((checkin, index) => ({
                ...checkin,
                id: `${docSnapshot.id}-${index}`,
                lineName: monthlyCheckins.line_name || routeId,
                routeId,
              }));
          });
        };

        const now = new Date();
        const currentYearMonth = format(now, 'yyyyMM');
        const previousYearMonth = format(subMonths(now, 1), 'yyyyMM');
        const currentMonthCheckins = await fetchCheckinsForMonth(currentYearMonth);
        const previousMonthCheckins = currentMonthCheckins.length < RECENT_CHECKIN_LIMIT
          ? await fetchCheckinsForMonth(previousYearMonth)
          : [];
        const checkins = [...currentMonthCheckins, ...previousMonthCheckins]
          .sort((a, b) => {
            const aTime = a.timestamp?.toMillis?.() ?? 0;
            const bTime = b.timestamp?.toMillis?.() ?? 0;
            return bTime - aTime;
          })
          .slice(0, RECENT_CHECKIN_LIMIT);

        const uniqueRouteIds = Array.from(new Set(checkins.map((checkin) => checkin.routeId)));
        const routeEntries = await Promise.all(
          uniqueRouteIds.map(async (routeId) => {
            const routeSnapshot = await getDoc(doc(db, 'routes', routeId));
            const routeName = routeSnapshot.exists()
              ? (routeSnapshot.data() as Route).name
              : undefined;

            return [routeId, routeName] as const;
          })
        );
        const routeNames = new Map(routeEntries);
        const items = checkins.map((checkin) => ({
          ...checkin,
          lineName: routeNames.get(checkin.routeId) || checkin.lineName,
        }));

        setRecentCheckins(items);
      } catch (error) {
        console.error('[최근 이용 내역] 조회 오류:', error);
        setRecentCheckins([]);
        setRecentCheckinsError('최근 이용 내역을 불러오는데 실패했습니다.');
      } finally {
        setRecentCheckinsLoading(false);
      }
    };

    fetchRecentCheckins();
  }, [inquiry?.userId]);

  // 답변 작성자 정보 조회
  useEffect(() => {
    const fetchAnsweredByName = async () => {
      if (!inquiry?.answeredBy) {
        setAnsweredByName('');
        setAnsweredByAdminType('');
        return;
      }

      try {
        // user_profiles 컬렉션에서 이름 정보 조회
        const userDoc = await getDoc(doc(db, 'user_profiles', inquiry.answeredBy));

        let displayName = inquiry.answeredBy;

        if (userDoc.exists()) {
          const userData = userDoc.data();

          // 우선순위: name -> organization_name -> email -> displayName -> UID
          const baseName =
            userData.name ||
            userData.organization_name ||
            userData.email ||
            userData.displayName ||
            inquiry.answeredBy;

          // 이름이 있으면 뒤에 UID 추가
          displayName = baseName !== inquiry.answeredBy
            ? `${baseName} (${inquiry.answeredBy})`
            : inquiry.answeredBy;
        }

        setAnsweredByName(displayName);

        // auth_profiles 컬렉션에서 adminType 정보 조회
        const authDoc = await getDoc(doc(db, 'auth_profiles', inquiry.answeredBy));

        if (authDoc.exists()) {
          const authData = authDoc.data();

          if (authData.adminType) {
            setAnsweredByAdminType(authData.adminType);
          } else {
            setAnsweredByAdminType('');
          }
        } else {
          setAnsweredByAdminType('');
        }
      } catch (error) {
        console.error('[답변 작성자] 조회 오류:', error);
        setAnsweredByName(inquiry.answeredBy);
        setAnsweredByAdminType('');
      }
    };

    fetchAnsweredByName();
  }, [inquiry?.answeredBy]);

  // 목록으로 돌아가기 함수
  const handleBackToList = () => {
    // 저장된 페이지 정보가 있으면 state와 함께 이동
    if (locationState?.page || locationState?.filter) {
      navigate('/complaints', {
        state: {
          page: locationState.page,
          filter: locationState.filter,
        },
      });
    } else {
      navigate('/complaints');
    }
  };

  const handleSubmitAnswer = async () => {
    if (!inquiry || !user || !inquiryId) return;

    if (!answer.trim()) {
      setError('답변 내용을 입력해주세요.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Firestore 업데이트
      await updateDoc(doc(db, 'inquiries', inquiryId), {
        answer: answer.trim(),
        answeredAt: new Date(),
        answeredBy: user.uid,
        isAnswered: true,
      });

      // FCM 푸시 알림 및 알림 내역 저장
      await sendInquiryAnswerNotification(inquiryId, inquiry.userId, inquiry.title);

      setSuccess('답변이 성공적으로 등록되었습니다.');

      // 2초 후 민원 관리 페이지로 이동 (저장된 페이지 정보와 함께)
      setTimeout(() => {
        handleBackToList();
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting answer:', err);
      setError('답변 등록에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePastInquiryClick = (pastInquiryId: string) => {
    navigate(`/cs/${pastInquiryId}`, {
      state: locationState || undefined,
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case '노선':
        return 'primary';
      case '서비스':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getAdminTypeLabel = (adminType: string): string => {
    switch (adminType) {
      case 'super':
        return '최고 관리자';
      case 'route1':
        return '강서구청 관리자';
      case 'route2':
        return '하단 관리자';
      default:
        return adminType;
    }
  };

  const getAdminTypeColor = (
    adminType: string
  ): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (adminType) {
      case 'super':
        return 'error';
      case 'route1':
      case 'route2':
        return 'primary';
      default:
        return 'default';
    }
  };

  const parseDiagnostics = (value: Inquiry['diagnostics']): InquiryDiagnostics | null => {
    if (!value) return null;

    if (typeof value === 'string') {
      try {
        return JSON.parse(value) as InquiryDiagnostics;
      } catch (error) {
        console.error('[diagnostics] parse error:', error);
        return null;
      }
    }

    return value;
  };

  const formatLabelValue = (value?: string | boolean | null) => {
    if (typeof value === 'boolean') {
      return value ? '활성화' : '비활성화';
    }

    if (value === undefined || value === null || value === '') {
      return '-';
    }

    return value;
  };

  const formatPermission = (value?: string) => {
    switch (value) {
      case 'granted':
        return '허용';
      case 'denied':
        return '거부';
      case 'blocked':
        return '차단';
      case 'unavailable':
        return '사용 불가';
      default:
        return formatLabelValue(value);
    }
  };

  const diagnostics = parseDiagnostics(inquiry?.diagnostics);
  const hasDiagnostics = adminType === 'super' && diagnostics && (
    diagnostics.app ||
    diagnostics.capturedAt ||
    diagnostics.device ||
    diagnostics.permissions ||
    diagnostics.services
  );

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!inquiry) {
    return (
      <MainLayout>
        <Box>
          <Alert severity="error">{error || '민원을 찾을 수 없습니다.'}</Alert>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            sx={{ mt: 2 }}
          >
            목록으로 돌아가기
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* 뒤로가기 버튼 */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToList}
          sx={{ mb: 3 }}
        >
          목록으로 돌아가기
        </Button>

        {/* 민원 정보 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {inquiry.title}
            </Typography>
            {inquiry.isAnswered && (
              <Chip
                icon={<CheckCircle />}
                label="답변완료"
                color="success"
                size="small"
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label={inquiry.category}
              color={getCategoryColor(inquiry.category)}
              size="small"
            />
            <Chip label={inquiry.route} variant="outlined" size="small" />
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              작성자 소속: {inquiry.organization || '미지정'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              작성일:{' '}
              {inquiry.createdAt
                ? format(inquiry.createdAt.toDate(), 'yyyy년 MM월 dd일 HH:mm', {
                    locale: ko,
                  })
                : '-'}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {inquiry.content}
          </Typography>

          {hasDiagnostics && (
            <>
              <Divider sx={{ my: 3 }} />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: isDiagnosticsOpen ? 2 : 0,
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  추가 내역
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setIsDiagnosticsOpen((prev) => !prev)}
                  aria-label={isDiagnosticsOpen ? '추가 내역 접기' : '추가 내역 펼치기'}
                >
                  {isDiagnosticsOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              <Collapse in={isDiagnosticsOpen}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      앱 정보
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      버전: {formatLabelValue(diagnostics.app?.version)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      빌드 번호: {formatLabelValue(diagnostics.app?.buildNumber)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      기기 정보
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      플랫폼: {formatLabelValue(diagnostics.device?.platform)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      OS 버전: {formatLabelValue(diagnostics.device?.osVersion)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      제조사: {formatLabelValue(diagnostics.device?.manufacturer)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      브랜드: {formatLabelValue(diagnostics.device?.brand)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      모델: {formatLabelValue(diagnostics.device?.model)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      디바이스 코드명: {formatLabelValue(diagnostics.device?.device)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      권한 상태
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      카메라: {formatPermission(diagnostics.permissions?.camera)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      위치: {formatPermission(diagnostics.permissions?.location)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      알림: {formatPermission(diagnostics.permissions?.notification)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      서비스 상태
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      위치 서비스: {formatLabelValue(diagnostics.services?.locationServiceEnabled)}
                    </Typography>
                  </Box>
                </Stack>
              </Collapse>
            </>
          )}
        </Paper>

        {/* 과거 민원 내역 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: isPastInquiriesOpen ? 2 : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                민원 내역
              </Typography>
              <Chip
                label={`${pastInquiries.length}건`}
                size="small"
                variant="outlined"
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsPastInquiriesOpen((prev) => !prev)}
              aria-label={isPastInquiriesOpen ? '과거 민원 내역 접기' : '과거 민원 내역 펼치기'}
            >
              {isPastInquiriesOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={isPastInquiriesOpen}>
            {pastInquiriesError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {pastInquiriesError}
              </Alert>
            )}

            {pastInquiriesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : pastInquiries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                과거 민원 내역이 없습니다.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>제목</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 180 }}>작성일</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pastInquiries.map((pastInquiry) => (
                      <TableRow
                        key={pastInquiry.id}
                        hover
                        onClick={() => handlePastInquiryClick(pastInquiry.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {pastInquiry.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {pastInquiry.createdAt
                              ? format(pastInquiry.createdAt.toDate(), 'yyyy-MM-dd HH:mm', {
                                  locale: ko,
                                })
                              : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Collapse>
        </Paper>

        {/* 최근 이용 내역 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: isRecentCheckinsOpen ? 2 : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                최근 이용 내역
              </Typography>
              <Chip
                label={`${recentCheckins.length}건`}
                size="small"
                variant="outlined"
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsRecentCheckinsOpen((prev) => !prev)}
              aria-label={isRecentCheckinsOpen ? '최근 이용 내역 접기' : '최근 이용 내역 펼치기'}
            >
              {isRecentCheckinsOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={isRecentCheckinsOpen}>
            {recentCheckinsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {recentCheckinsError}
              </Alert>
            )}

            {recentCheckinsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : recentCheckins.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                최근 이용 내역이 없습니다.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>이용 노선</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 100 }}>이용유형</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>이용 정류장</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>승차 정류장</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>하차 정류장</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 180 }}>탑승 일시</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentCheckins.map((checkin) => (
                      <TableRow key={checkin.id} hover>
                        <TableCell>{checkin.lineName || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={checkin.type === 'reservation' ? '예약' : 'QR'}
                            color={checkin.type === 'reservation' ? 'secondary' : 'primary'}
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 52, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>{checkin.station_name || '-'}</TableCell>
                        <TableCell>
                          {checkin.type === 'reservation' ? checkin.boarding_station || '-' : '-'}
                        </TableCell>
                        <TableCell>
                          {checkin.type === 'reservation' ? checkin.alighting_station || '-' : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {checkin.timestamp?.toDate
                              ? format(checkin.timestamp.toDate(), 'yyyy-MM-dd HH:mm', {
                                  locale: ko,
                                })
                              : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Collapse>
        </Paper>

        {/* 답변 섹션 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            답변 작성
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={8}
            placeholder="답변 내용을 입력하세요..."
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={submitting || inquiry.isAnswered}
            sx={{ mb: 2 }}
          />

          {inquiry.isAnswered && inquiry.answeredAt && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                답변 등록일:{' '}
                {format(inquiry.answeredAt.toDate(), 'yyyy년 MM월 dd일 HH:mm', {
                  locale: ko,
                })}
              </Typography>
              {answeredByName && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  답변 작성자: {answeredByName}
                </Typography>
              )}
              {answeredByAdminType && (
                <Chip
                  label={getAdminTypeLabel(answeredByAdminType)}
                  color={getAdminTypeColor(answeredByAdminType)}
                  size="small"
                  sx={{ ml: 1, height: '20px', fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              onClick={handleSubmitAnswer}
              disabled={submitting || inquiry.isAnswered || !answer.trim()}
              size="large"
            >
              {inquiry.isAnswered ? '답변 완료' : '답변 등록'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  );
};
